import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Input,
  InputNumber,
  Tag,
  Typography,
  message,
  Modal,
} from 'antd';
import { useVirtualizer } from '@tanstack/react-virtual';
import { formatMoney } from '@/utils/format';
import { getEst3CostProvenance } from '../est3CostProvenance';
import {
  PASTE_FIELD_ORDER,
  toClipboardTsv,
  type EstimatingEditableField,
} from './estimatingClipboard';
import { previewPasteOntoBoqRows, type DirtyBoqPatch } from './estimatingPastePreview';
import {
  ESTIMATING_GRID_COLUMNS,
  buildGridTemplate,
  loadVisibleColumns,
  lockedColumnIds,
  persistVisibleColumns,
  type EstimatingGridColumnId,
} from './estimatingGridColumns';
import styles from './EpcEstimatingWorkbench.module.css';

const { Text } = Typography;
const ROW_H = 36;

export type BoqRollup = {
  line_count: number;
  total_amount: number;
  sell_amount?: number;
  margin_pct?: number | null;
  coverage_status?: string;
};

export interface EpcEstimatingBoqGridProps {
  lines: any[];
  loading?: boolean;
  readOnly?: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  boqRollups?: Map<string, BoqRollup>;
  coverageFilter?: 'all' | 'without_cost' | 'unlinked';
  onCoverageFilterChange?: (f: 'all' | 'without_cost' | 'unlinked') => void;
  healthLineIds?: Set<string>;
  onBatchSave: (
    patches: Array<{
      lineId: string;
      expectedUpdatedAt?: string | null;
      quantity?: number;
      unitRate?: number;
      description?: string;
      notes?: string;
      uom?: string;
    }>
  ) => Promise<{ updated: number; failed: number; results?: any[] }>;
  savePending?: boolean;
  jumpToId?: string | null;
  toolbarExtra?: React.ReactNode;
}

type FlatRow = {
  id: string;
  depth: number;
  hasChildren: boolean;
  raw: any;
};

function buildFlat(
  lines: any[],
  expanded: Set<string>,
  filter: string,
  coverageFilter: string,
  rollups?: Map<string, BoqRollup>
): FlatRow[] {
  const byParent = new Map<string | null, any[]>();
  for (const row of lines) {
    const p = (row.parent_id || row.parentId || null) as string | null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p)!.push(row);
  }
  for (const list of byParent.values()) {
    list.sort(
      (a, b) =>
        Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
        String(a.item_number || '').localeCompare(String(b.item_number || ''))
    );
  }

  const q = filter.trim().toLowerCase();
  const out: FlatRow[] = [];

  const matches = (row: any) => {
    if (q) {
      const hay = `${row.item_number || ''} ${row.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (coverageFilter === 'without_cost' && rollups) {
      const s = rollups.get(row.id)?.coverage_status;
      return s === 'no_cost' || !rollups.has(row.id);
    }
    return true;
  };

  const subtreeHasMatch = (id: string): boolean => {
    if (matches((lines || []).find((r) => r.id === id) || { id })) return true;
    for (const child of byParent.get(id) || []) {
      if (subtreeHasMatch(child.id)) return true;
    }
    return false;
  };

  const walk = (parentId: string | null, depth: number) => {
    for (const row of byParent.get(parentId) || []) {
      const kids = byParent.get(row.id) || [];
      const selfOk = matches(row);
      const childOk = q ? kids.some((c) => subtreeHasMatch(c.id)) : kids.length > 0;
      if (!selfOk && !(q && childOk)) continue;
      if (coverageFilter === 'without_cost' && !selfOk && !q) continue;
      out.push({
        id: row.id,
        depth,
        hasChildren: kids.length > 0,
        raw: row,
      });
      const shouldExpand = expanded.has(row.id) || (q && childOk);
      if (kids.length && shouldExpand) walk(row.id, depth + 1);
    }
  };

  walk(null, 0);
  return out;
}

export const EpcEstimatingBoqGrid: React.FC<EpcEstimatingBoqGridProps> = ({
  lines,
  loading,
  readOnly,
  selectedId,
  onSelect,
  boqRollups,
  coverageFilter = 'all',
  onCoverageFilterChange,
  healthLineIds,
  onBatchSave,
  savePending,
  jumpToId,
  toolbarExtra,
}) => {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [dirty, setDirty] = useState<Map<string, DirtyBoqPatch>>(() => new Map());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [editCell, setEditCell] = useState<{ id: string; field: EstimatingEditableField } | null>(
    null
  );
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteIssues, setPasteIssues] = useState<string[]>([]);
  const [pendingPaste, setPendingPaste] = useState<Map<string, DirtyBoqPatch> | null>(null);
  const [visibleCols, setVisibleCols] = useState<EstimatingGridColumnId[]>(() => loadVisibleColumns());
  const parentRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLInputElement | null>(null);

  const visibleSet = useMemo(() => new Set(visibleCols), [visibleCols]);
  const gridTemplate = useMemo(() => buildGridTemplate(visibleSet), [visibleSet]);
  const show = (id: EstimatingGridColumnId) => visibleSet.has(id);

  const setColumns = (ids: EstimatingGridColumnId[]) => {
    const locked = lockedColumnIds();
    const next = [...new Set([...locked, ...ids])] as EstimatingGridColumnId[];
    setVisibleCols(next);
    persistVisibleColumns(next);
  };

  const flat = useMemo(
    () => buildFlat(lines || [], expanded, filter, coverageFilter, boqRollups),
    [lines, expanded, filter, coverageFilter, boqRollups]
  );

  useEffect(() => {
    // expand roots by default once
    if (expanded.size === 0 && lines?.length) {
      const roots = (lines || []).filter((r) => !(r.parent_id || r.parentId));
      setExpanded(new Set(roots.slice(0, 40).map((r) => r.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines?.length]);

  useEffect(() => {
    if (!jumpToId) return;
    onSelect(jumpToId);
    const idx = flat.findIndex((r) => r.id === jumpToId);
    if (idx >= 0) {
      virtualizer.scrollToIndex(idx, { align: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToId]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.size) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty.size]);

  const virtualizer = useVirtualizer({
    count: flat.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });

  const getEffective = useCallback(
    (row: any, field: EstimatingEditableField) => {
      const d = dirty.get(row.id);
      if (field === 'quantity') return d?.quantity ?? Number(row.quantity ?? 0);
      if (field === 'unit_rate') return d?.unit_rate ?? Number(row.unit_rate ?? 0);
      if (field === 'description') return d?.description ?? String(row.description ?? '');
      if (field === 'notes') return d?.notes ?? String(row.notes ?? '');
      if (field === 'uom') return d?.uom ?? String(row.uom ?? '');
      return '';
    },
    [dirty]
  );

  const setField = (id: string, field: EstimatingEditableField, value: string | number) => {
    setDirty((prev) => {
      const next = new Map(prev);
      const cur = { ...(next.get(id) || {}) };
      if (field === 'quantity') cur.quantity = Number(value);
      else if (field === 'unit_rate') cur.unit_rate = Number(value);
      else if (field === 'description') cur.description = String(value);
      else if (field === 'notes') cur.notes = String(value);
      else if (field === 'uom') cur.uom = String(value);
      next.set(id, cur);
      return next;
    });
  };

  const saveDirty = async () => {
    if (!dirty.size) return;
    const patches = [...dirty.entries()].map(([lineId, p]) => {
      const raw = (lines || []).find((r) => r.id === lineId);
      return {
        lineId,
        expectedUpdatedAt: raw?.updated_at ? String(raw.updated_at) : null,
        quantity: p.quantity,
        unitRate: p.unit_rate,
        description: p.description,
        notes: p.notes,
        uom: p.uom,
      };
    });
    const res = await onBatchSave(patches);
    const failed = (res.results || []).filter((r: any) => !r.ok);
    if (res.failed === 0) {
      setDirty(new Map());
      message.success(`Saved ${res.updated} line(s)`);
    } else {
      const staleIds = new Set(
        (res.results || []).filter((r: any) => !r.ok && r.code === 'STALE').map((r: any) => r.lineId)
      );
      setDirty((prev) => {
        const next = new Map(prev);
        for (const r of res.results || []) {
          if (r.ok) next.delete(r.lineId);
          // keep stale / failed patches so user does not lose edits
        }
        return next;
      });
      if (staleIds.size) {
        message.error(
          `${staleIds.size} row(s) changed by another user. Your edits are kept — refresh those rows and retry.`
        );
      } else {
        message.warning(`Saved ${res.updated}, ${res.failed} failed`);
      }
    }
  };

  const copySelection = async () => {
    const ids = selectedIds.size ? [...selectedIds] : selectedId ? [selectedId] : [];
    const rows = ids
      .map((id) => flat.find((f) => f.id === id)?.raw)
      .filter(Boolean)
      .map((r: any) => [
        r.item_number,
        getEffective(r, 'description'),
        getEffective(r, 'quantity'),
        getEffective(r, 'unit_rate'),
        getEffective(r, 'uom'),
      ]);
    if (!rows.length) {
      message.info('Select rows to copy');
      return;
    }
    await navigator.clipboard.writeText(toClipboardTsv(rows));
    message.success(`Copied ${rows.length} row(s)`);
  };

  const handlePaste = async (e?: React.ClipboardEvent) => {
    if (readOnly) return;
    const text =
      e?.clipboardData?.getData('text/plain') ||
      (await navigator.clipboard.readText().catch(() => ''));
    if (!text?.trim()) return;
    e?.preventDefault();
    const startId = selectedId || flat[0]?.id;
    const startIdx = Math.max(
      0,
      flat.findIndex((r) => r.id === startId)
    );
    const startField = editCell?.field || 'quantity';
    const preview = previewPasteOntoBoqRows(
      flat.map((f) => ({ id: f.id })),
      startIdx,
      startField,
      text,
      PASTE_FIELD_ORDER
    );
    setPasteIssues(preview.issues.map((i) => `${i.rowId || '—'} ${i.field}: ${i.message}`));
    setPendingPaste(preview.patches);
    setPasteOpen(true);
  };

  const applyPaste = () => {
    if (!pendingPaste) return;
    setDirty((prev) => {
      const next = new Map(prev);
      for (const [id, p] of pendingPaste) {
        next.set(id, { ...(next.get(id) || {}), ...p });
      }
      return next;
    });
    setPasteOpen(false);
    setPendingPaste(null);
    message.success(`Paste staged on ${pendingPaste.size} row(s) — Save to persist`);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void saveDirty();
      return;
    }
    if (e.key === 'c' && (e.ctrlKey || e.metaKey) && !editCell) {
      e.preventDefault();
      void copySelection();
      return;
    }
    if (!selectedId) return;
    const idx = flat.findIndex((r) => r.id === selectedId);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = flat[idx + 1];
      if (next) onSelect(next.id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = flat[idx - 1];
      if (prev) onSelect(prev.id);
    } else if (e.key === 'Enter' && !readOnly) {
      e.preventDefault();
      setEditCell({ id: selectedId, field: 'quantity' });
    } else if (e.key === 'Tab' && editCell) {
      e.preventDefault();
      const order = PASTE_FIELD_ORDER;
      const fi = order.indexOf(editCell.field);
      if (e.shiftKey) {
        if (fi > 0) setEditCell({ id: editCell.id, field: order[fi - 1] });
        else if (idx > 0) setEditCell({ id: flat[idx - 1].id, field: order[order.length - 1] });
      } else if (fi < order.length - 1) {
        setEditCell({ id: editCell.id, field: order[fi + 1] });
      } else if (idx < flat.length - 1) {
        setEditCell({ id: flat[idx + 1].id, field: order[0] });
        onSelect(flat[idx + 1].id);
      }
    }
  };

  useEffect(() => {
    if (editCell) editRef.current?.focus();
  }, [editCell]);

  return (
    <div className={styles.gridPane} tabIndex={0} onKeyDown={onKeyDown} onPaste={handlePaste}>
      <div className={styles.gridToolbar}>
        <div className={styles.gridToolbarLeft}>
          <Input.Search
            allowClear
            size="small"
            placeholder="Search item / description"
            style={{ width: 200, maxWidth: '36vw', flexShrink: 1 }}
            onSearch={setFilter}
            onChange={(e) => {
              if (!e.target.value) setFilter('');
            }}
          />
          <Button.Group size="small">
            <Button
              type={coverageFilter === 'all' ? 'primary' : 'default'}
              onClick={() => onCoverageFilterChange?.('all')}
            >
              All
            </Button>
            <Button
              type={coverageFilter === 'without_cost' ? 'primary' : 'default'}
              onClick={() => onCoverageFilterChange?.('without_cost')}
            >
              Unpriced
            </Button>
          </Button.Group>
          {toolbarExtra}
          <Text type="secondary" ellipsis style={{ maxWidth: 220 }}>
            {flat.length} shown · {lines?.length || 0} total
            {dirty.size ? ` · ${dirty.size} dirty` : ''}
            {selectedIds.size ? ` · ${selectedIds.size} selected` : ''}
          </Text>
        </div>
        <div className={styles.gridToolbarRight}>
          <Dropdown
            trigger={['click']}
            dropdownRender={() => (
              <div
                data-testid="est-column-visibility"
                style={{
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  padding: 12,
                  minWidth: 200,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <Text strong style={{ fontSize: 12 }}>
                  Columns
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Checkbox.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                    value={visibleCols}
                    onChange={(vals) => setColumns(vals as EstimatingGridColumnId[])}
                    options={ESTIMATING_GRID_COLUMNS.map((c) => ({
                      label: c.locked ? `${c.label} (required)` : c.label,
                      value: c.id,
                      disabled: c.locked,
                    }))}
                  />
                </div>
              </div>
            )}
          >
            <Button size="small" data-testid="est-columns-btn">
              Columns
            </Button>
          </Dropdown>
          <Button size="small" onClick={() => void copySelection()}>
            Copy
          </Button>
          <Button size="small" disabled={!!readOnly} onClick={() => void handlePaste()}>
            Paste
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={!!readOnly || !dirty.size}
            loading={savePending}
            onClick={() => void saveDirty()}
          >
            Save{dirty.size ? ` (${dirty.size})` : ''}
          </Button>
        </div>
      </div>

      {readOnly ? (
        <Alert
          type="info"
          showIcon
          banner
          message="This revision is read-only — commercial numbers cannot be edited"
        />
      ) : null}

      <div ref={parentRef} className={styles.gridScroll} data-testid="est-boq-grid">
        <div className={styles.gridHeader} style={{ gridTemplateColumns: gridTemplate }}>
          {show('select') ? <div className={styles.cell} /> : null}
          {show('expand') ? <div className={styles.cell} /> : null}
          {show('item') ? <div className={styles.cell}>Item</div> : null}
          {show('description') ? <div className={styles.cell}>Description</div> : null}
          {show('uom') ? <div className={styles.cell}>UOM</div> : null}
          {show('quantity') ? <div className={styles.cell}>Qty</div> : null}
          {show('rate') ? <div className={styles.cell}>Rate</div> : null}
          {show('amount') ? <div className={styles.cell}>Amount</div> : null}
          {show('links') ? <div className={styles.cell}>Links</div> : null}
          {show('coverage') ? <div className={styles.cell}>Cov</div> : null}
          {show('cost') ? <div className={styles.cell}>Cost</div> : null}
        </div>
        {loading && !flat.length ? (
          <div style={{ padding: 24 }}>
            <Text type="secondary">Loading BOQ…</Text>
          </div>
        ) : !flat.length ? (
          <div style={{ padding: 24 }}>
            <Text type="secondary">No BOQ lines — import Excel or add a line to begin.</Text>
          </div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {virtualizer.getVirtualItems().map((vi) => {
              const row = flat[vi.index]!;
              const raw = row.raw;
              const roll = boqRollups?.get(row.id);
              const isSel = selectedId === row.id || selectedIds.has(row.id);
              const isDirty = dirty.has(row.id);
              const isWarn = healthLineIds?.has(row.id);
              const qty = getEffective(raw, 'quantity') as number;
              const rate = getEffective(raw, 'unit_rate') as number;
              const amount = Math.round(Number(qty) * Number(rate) * 100) / 100;
              const cov = roll?.coverage_status;
              return (
                <div
                  key={row.id}
                  className={[
                    styles.gridRow,
                    isSel ? styles.gridRowSelected : '',
                    isDirty ? styles.gridRowDirty : '',
                    isWarn ? styles.gridRowWarn : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: ROW_H,
                    transform: `translateY(${vi.start}px)`,
                    gridTemplateColumns: gridTemplate,
                  }}
                  data-testid={`est-boq-row-${row.id}`}
                  data-dirty={isDirty ? '1' : '0'}
                  onClick={(ev) => {
                    if (ev.ctrlKey || ev.metaKey) {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.id)) next.delete(row.id);
                        else next.add(row.id);
                        return next;
                      });
                    }
                    onSelect(row.id);
                  }}
                >
                  {show('select') ? (
                  <div className={styles.cell}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(row.id);
                          else next.delete(row.id);
                          return next;
                        });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  ) : null}
                  {show('expand') ? (
                  <div className={styles.cell}>
                    {row.hasChildren ? (
                      <button
                        type="button"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(row.id);
                        }}
                      >
                        {expanded.has(row.id) ? '▾' : '▸'}
                      </button>
                    ) : null}
                  </div>
                  ) : null}
                  {show('item') ? (
                  <div className={styles.cell} style={{ paddingLeft: 6 + row.depth * 12 }}>
                    {raw.item_number || '—'}
                  </div>
                  ) : null}
                  {show('description') ? (
                  <div
                    className={styles.cell}
                    onDoubleClick={() =>
                      !readOnly && setEditCell({ id: row.id, field: 'description' })
                    }
                  >
                    {editCell?.id === row.id && editCell.field === 'description' ? (
                      <input
                        ref={editRef}
                        className={styles.cellInput}
                        defaultValue={String(getEffective(raw, 'description'))}
                        onBlur={(e) => {
                          setField(row.id, 'description', e.target.value);
                          setEditCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') setEditCell(null);
                        }}
                      />
                    ) : (
                      getEffective(raw, 'description')
                    )}
                  </div>
                  ) : null}
                  {show('uom') ? <div className={styles.cell}>{getEffective(raw, 'uom') || '—'}</div> : null}
                  {show('quantity') ? (
                  <div
                    className={styles.cell}
                    onDoubleClick={() => !readOnly && setEditCell({ id: row.id, field: 'quantity' })}
                  >
                    {editCell?.id === row.id && editCell.field === 'quantity' ? (
                      <InputNumber
                        size="small"
                        autoFocus
                        style={{ width: '100%' }}
                        value={Number(getEffective(raw, 'quantity'))}
                        min={0}
                        onChange={(v) => setField(row.id, 'quantity', Number(v || 0))}
                        onBlur={() => setEditCell(null)}
                        onPressEnter={() => setEditCell(null)}
                      />
                    ) : (
                      qty
                    )}
                  </div>
                  ) : null}
                  {show('rate') ? (
                  <div
                    className={styles.cell}
                    onDoubleClick={() =>
                      !readOnly && setEditCell({ id: row.id, field: 'unit_rate' })
                    }
                  >
                    {editCell?.id === row.id && editCell.field === 'unit_rate' ? (
                      <InputNumber
                        size="small"
                        autoFocus
                        style={{ width: '100%' }}
                        value={Number(getEffective(raw, 'unit_rate'))}
                        min={0}
                        onChange={(v) => setField(row.id, 'unit_rate', Number(v || 0))}
                        onBlur={() => setEditCell(null)}
                        onPressEnter={() => setEditCell(null)}
                      />
                    ) : (
                      formatMoney(rate, { display: 'plain', maximumFractionDigits: 2 })
                    )}
                  </div>
                  ) : null}
                  {show('amount') ? (
                  <div className={styles.cell}>
                    {formatMoney(amount, { display: 'plain', maximumFractionDigits: 0 })}
                  </div>
                  ) : null}
                  {show('links') ? <div className={styles.cell}>{roll?.line_count ?? '—'}</div> : null}
                  {show('coverage') ? (
                  <div className={styles.cell}>
                    {!cov || cov === 'linked' ? (
                      <Tag color="green">OK</Tag>
                    ) : cov === 'no_cost' ? (
                      <Tag color="orange">Unpriced</Tag>
                    ) : (
                      <Tag>{cov}</Tag>
                    )}
                  </div>
                  ) : null}
                  {show('cost') ? (
                  <div className={styles.cell}>
                    {roll
                      ? formatMoney(roll.total_amount, {
                          display: 'plain',
                          maximumFractionDigits: 0,
                        })
                      : '—'}
                  </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        title="Paste preview"
        open={pasteOpen}
        onCancel={() => {
          setPasteOpen(false);
          setPendingPaste(null);
        }}
        onOk={applyPaste}
        okText={`Apply to ${pendingPaste?.size || 0} rows`}
        okButtonProps={{ disabled: !pendingPaste?.size }}
      >
        <Text>
          Staged edits will mark rows dirty. Authoritative totals refresh after Save (backend TEC).
        </Text>
        {pasteIssues.length ? (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Validation notes"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {pasteIssues.slice(0, 12).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            }
          />
        ) : (
          <Alert style={{ marginTop: 12 }} type="success" showIcon message="No blocking issues" />
        )}
      </Modal>
    </div>
  );
};

/** Re-export helper for inspector provenance peek on first linked cost. */
export function summarizeFirstCostProvenance(costLines: any[]): string {
  const first = costLines?.[0];
  if (!first) return 'No linked cost yet';
  return getEst3CostProvenance(first).summary;
}
